import { type CardSlotUiView, pvpCardSlotViews } from '@kenocyber/game-domain';
import { emptyPvpCardSlots, formatHealthMilli } from '@kenocyber/shared';
import {
  type PvpConnectionTone,
  type PvpEffectBadge,
  type PvpHealthBar,
  pvpEffectBadges,
  pvpTimeLabel,
} from './hud';
import type { PvpTeamClientState } from './team-connection';
import type { PvpTeamUiState } from './team-machine';
import { type PvpResultText, pvpTeamResultText } from './team-result-text';

/**
 * The `team_3v3` interface, as one value derived from one state.
 *
 * ## Where every number comes from
 *
 * | Shown | Source |
 * | --- | --- |
 * | health, all six | `state` frame, `parts[].hp` |
 * | maximum health | `welcome.maxHealth` |
 * | time remaining | `state` frame, `left` |
 * | who is on my side | `you.tm` against `parts[].tm` |
 * | result | `ended` frame, `winnerTeam`, and nothing else |
 *
 * Every one of those is the SERVER's. Nothing here adds, subtracts, smooths or
 * predicts.
 *
 * ## Six bars, split by the AUTHORITATIVE team
 *
 * The duel's `pvpHudView` answers `you` and `them` because a duel has two ships.
 * Here the split is by `part.tm` against this client's own `you.tm`, so the six
 * browsers receive one frame and each of them draws its own side on its own
 * half. Never by chair order, never by the order the room happened to list the
 * participants in, and never by the slot number - a roster that arrives in
 * another order must produce exactly the same screen.
 *
 * ## Nothing identifying is in here
 *
 * No ticket, no account, no match id, no code, no e-mail. What reaches a screen
 * is health, time, a phase, a chair label and a result.
 */

export interface PvpTeamFighterView {
  /** The participant id the room named. The React key, and never an index. */
  readonly participantId: string;
  /** `1`..`3`, as the server assigned it. Shown so six bars can be told apart. */
  readonly slot: number;
  /** Whether this is the browser looking at the screen. */
  readonly local: boolean;
  readonly connected: boolean;
  readonly health: PvpHealthBar;
}

export interface PvpTeamHudView {
  readonly phaseLabel: string;
  /** This client's own side, by slot. Empty before the first frame. */
  readonly allies: readonly PvpTeamFighterView[];
  /** The other side, by slot. */
  readonly enemies: readonly PvpTeamFighterView[];
  readonly timeLabel: string | null;
  readonly connection: PvpConnectionTone;
  readonly connectionLabel: string;
  readonly controlsEnabled: boolean;
  readonly prepareLabel: string | null;
  readonly reconnecting: boolean;
  readonly result: PvpResultText | null;
  readonly noRewardNotice: string;
  /** What the LOCAL chair is carrying. Empty whenever there is nothing. */
  readonly effects: readonly PvpEffectBadge[];
  /**
   * Quantas marcas de sangramento ESTE jogador tem espalhadas (V28G.N).
   *
   * Filtradas pela autoria, e é aqui que isso importa mais do que no duelo: num
   * 3x3 as marcas de dois aliados estão sobre os mesmos três inimigos, e contar
   * a lista inteira daria a este jogador um botão que promete rebentar o que ele
   * não pode.
   */
  readonly bleedMarks: number;
  /** Se o botão DETONAR pode ser premido: há marcas, e este casco está vivo. */
  readonly canDetonate: boolean;
  /** Os três espaços desta cadeira (gate V28H.J). Ver o duelo. */
  readonly cardSlots: readonly CardSlotUiView[];
}

const NO_REWARD = 'SEM RECOMPENSA';

/**
 * A bar, clamped at both ends, em MILLI (gate V28G.S.1).
 *
 * A razão exata entre a vida corrente e o teto deste casco, nos dois em
 * milésimos - e o texto sai do formatador canónico, nunca de uma divisão local.
 */
function bar(current: bigint, max: bigint): PvpHealthBar {
  const safeMax = max > 0n ? max : 0n;
  const clamped = current < 0n ? 0n : current;
  const safeCurrent = clamped > safeMax ? safeMax : clamped;
  return {
    currentMilli: safeCurrent,
    maxMilli: safeMax,
    percent: safeMax === 0n ? 0 : Number((safeCurrent * 100n) / safeMax),
    currentText: formatHealthMilli(safeCurrent),
    maxText: formatHealthMilli(safeMax),
  };
}

/**
 * The slot a participant id names, for ORDERING six bars and nothing else.
 *
 * The id is `a1`..`b3` by contract and the digit is its slot. It decides the
 * order two bars appear in on a screen and reaches no other decision: who is an
 * ally comes from `team`, which the room states, and a parse that produced the
 * wrong number here would move a bar rather than change a side.
 */
function slotOf(participantId: string): number {
  const digit = Number.parseInt(participantId.slice(1), 10);
  return Number.isFinite(digit) ? digit : 0;
}

/**
 * Six bars, split by the room's own team letters.
 *
 * `localTeam` is `you.tm`, straight from the frame this client received - and
 * when it is absent nothing is split at all, because a client that does not know
 * its own side must not guess one and draw three enemies as friends.
 */
export function pvpTeamFighters(
  ships: readonly {
    readonly s: string;
    readonly team?: string;
    /** A vida deste casco, em MILLI, como string decimal (gate V28G.S.1). */
    readonly hp: string;
    readonly c: boolean;
  }[],
  localParticipantId: string | null,
  localTeam: string | null,
  /**
   * Each hull's own ceiling, keyed by participant id.
   *
   * It used to be one `maxHealth` for all six, which was right exactly while
   * every hull was identical - the assumption this protocol version stops
   * making. Six ships drawn against one denominator would show a tougher
   * teammate as permanently wounded.
   *
   * A participant the roster has not described yet reads zero, and `bar`
   * renders a zero maximum as empty rather than dividing by it.
   */
  ceilingOf: (participantId: string) => bigint,
): { allies: PvpTeamFighterView[]; enemies: PvpTeamFighterView[] } {
  if (localTeam === null) {
    return { allies: [], enemies: [] };
  }
  const allies: PvpTeamFighterView[] = [];
  const enemies: PvpTeamFighterView[] = [];
  for (const ship of ships) {
    const view: PvpTeamFighterView = {
      participantId: ship.s,
      slot: slotOf(ship.s),
      local: ship.s === localParticipantId,
      connected: ship.c,
      health: bar(BigInt(ship.hp), ceilingOf(ship.s)),
    };
    // The AUTHORITY's team, never the id's first character and never the order
    // this ship arrived in.
    (ship.team === localTeam ? allies : enemies).push(view);
  }
  const bySlot = (left: PvpTeamFighterView, right: PvpTeamFighterView) => left.slot - right.slot;
  return { allies: allies.sort(bySlot), enemies: enemies.sort(bySlot) };
}

/**
 * The whole HUD, for any state the team machine can be in.
 *
 * Exhaustive: the fallthrough is `never`, so a fourteenth state stops the build
 * rather than rendering whatever the nearest case happens to be.
 */
export function pvpTeamHudView(state: PvpTeamUiState): PvpTeamHudView {
  const base = {
    allies: [] as readonly PvpTeamFighterView[],
    enemies: [] as readonly PvpTeamFighterView[],
    timeLabel: null,
    connection: 'live' as PvpConnectionTone,
    connectionLabel: 'Conectado',
    controlsEnabled: false,
    prepareLabel: null,
    reconnecting: false,
    result: null,
    noRewardNotice: NO_REWARD,
    effects: [] as readonly PvpEffectBadge[],
    bleedMarks: 0,
    canDetonate: false,
    /* Três vazios: é o que se vê antes de uma partida existir. */
    cardSlots: pvpCardSlotViews(emptyPvpCardSlots(), { alive: false, inMatch: false }),
  };

  switch (state.kind) {
    case 'closed':
    case 'choosing':
      return { ...base, phaseLabel: 'Batalha em equipe 3x3' };
    case 'creating':
      return { ...base, phaseLabel: 'Criando a sala' };
    case 'joining':
      return { ...base, phaseLabel: 'Entrando na sala' };
    case 'lobby':
      return { ...base, phaseLabel: 'Montando as equipes' };
    case 'connecting':
      return { ...base, phaseLabel: 'Conectando', connectionLabel: 'Conectando…' };
    case 'entering_pvp':
      return { ...base, phaseLabel: 'Entrando na arena', prepareLabel: 'PREPARANDO A PARTIDA' };

    case 'ready': {
      const teams = fightersOf(state);
      return {
        ...base,
        ...teams,
        phaseLabel: state.declared ? 'Pronto' : 'Equipes completas',
        prepareLabel: 'PREPARANDO A PARTIDA',
      };
    }

    case 'running': {
      const teams = fightersOf(state);
      const world = state.connection.world;
      const me = state.connection.you?.p ?? null;
      /* As marcas DESTE jogador, pelo mesmo filtro que a sala usa para as rebentar. */
      const marks =
        me === null
          ? 0
          : world.bleeding.reduce((total, mark) => total + (mark.p === me ? 1 : 0), 0);
      /* Vivo, lido na barra que a sala publicou e nunca numa segunda fonte. */
      const mine = me === null ? undefined : world.ships.find((ship) => ship.s === me);
      const alive = mine === undefined ? false : BigInt(mine.hp) > 0n;
      return {
        ...base,
        ...teams,
        phaseLabel: 'Em combate',
        bleedMarks: marks,
        canDetonate: marks > 0 && alive,
        /* Os três espaços, com a vida a decidir o bloqueio (gate V28H.J). */
        cardSlots: pvpCardSlotViews(state.connection.you?.ck ?? emptyPvpCardSlots(), {
          alive,
          inMatch: true,
        }),
        // The server's own remaining milliseconds. No local clock anywhere.
        timeLabel: pvpTimeLabel(world.msLeft),
        controlsEnabled: true,
        effects: pvpEffectBadges(
          world.effects,
          state.connection.you?.p ?? null,
          world.arena?.tickMs ?? 50,
        ),
      };
    }

    case 'reconnecting': {
      const teams = fightersOf(state);
      return {
        ...base,
        ...teams,
        /*
         * Not "Reconectando" - the alert beside it already says that. This says
         * the other true thing: what is on screen is the last frame the server
         * confirmed, and nothing has moved since.
         */
        phaseLabel: 'Último estado confirmado',
        /*
         * The last authoritative time, frozen. It does not tick down while the
         * socket is away: the client has no authority to advance it.
         */
        timeLabel: pvpTimeLabel(state.connection.world.msLeft),
        connection: 'reconnecting',
        connectionLabel: 'Reconectando…',
        reconnecting: true,
      };
    }

    case 'completed':
      return {
        ...base,
        phaseLabel: 'Partida encerrada',
        connection: 'closed',
        connectionLabel: 'Encerrado',
        /*
         * The only road to a result, and it is read against the team AND the
         * chair the SERVER put in this client's own `you` - never the roster's
         * order, never the last hull left on screen. The chair is what tells
         * "I left" from "a team-mate left", and only the room knows which.
         */
        result: pvpTeamResultText(state.result, state.match.team, state.match.participantId),
      };

    case 'leaving':
      return { ...base, phaseLabel: 'Encerrando', connection: 'closed', connectionLabel: '—' };

    case 'recovering_hunt':
      return {
        ...base,
        phaseLabel: 'Retornando ao jogo',
        connection: 'closed',
        connectionLabel: '—',
      };

    case 'error':
      return {
        ...base,
        phaseLabel: 'Partida interrompida',
        connection: 'closed',
        connectionLabel: '—',
      };

    default: {
      const unhandled: never = state;
      throw new Error(`unhandled pvp team ui state: ${JSON.stringify(unhandled)}`);
    }
  }
}

/** The six bars for a state that holds a connection. */
function fightersOf(state: { readonly connection: PvpTeamClientState }): {
  allies: readonly PvpTeamFighterView[];
  enemies: readonly PvpTeamFighterView[];
} {
  const { world, you, roster } = state.connection;
  /*
   * From the ROSTER, which is where the room states what each hull is. Not
   * from the per-tick participant frame: a profile changes when the room says
   * so and never inside a tick, so repeating four integers six times a tick
   * would pay twenty bytes eighteen hundred times a minute to say nothing new.
   */
  const ceilings = new Map(roster.map((entry) => [entry.p, BigInt(entry.cp.maxHealthMilli)]));
  return pvpTeamFighters(
    world.ships,
    you?.p ?? null,
    you?.tm ?? null,
    (participantId) => ceilings.get(participantId) ?? 0n,
  );
}
